import { useMemo, useState, type RefObject } from 'react';
import type { LayerSpecification } from 'mapbox-gl';
import { UMR, type UMMapHandle } from '../../urbanmap/_components';
import { useBuildingUpload } from './selection/BuildingUpload';
import { DensityLegendPanel } from './selection/DensityLegendPanel';
import { NETWORK_LAYERS, networkSource, useNetworkMap } from './selection/NetworkMap';
import type { Coordinate } from './selection/network';
import { SelectionFormPanel } from './selection/SelectionFormPanel';
import { SelectionPanel } from './selection/SelectionPanel';
import { useNetwork } from './selection/useNetwork';
import { useSelection } from './selection/useSelection';
import type { SelectionDraft } from './selection/types';
import type { PlanBuilding } from './types';

const centroid = (b: PlanBuilding): Coordinate => {
  const ring = (b.geometry.type === 'Polygon' ? b.geometry.coordinates[0] : b.geometry.coordinates[0][0]) as Coordinate[];
  const pts = ring.slice(0, -1);
  return [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
};

/**
 * Panneau « Sélection de batiments » repris du prototype `urbanmap/nouveau-panel-selection`,
 * branché sur les bâtiments du projet Plan. Sélections Demo 2 / Demo 3 : bâtiments de démonstration.
 */
export function useProjectSelection(mapRef: RefObject<UMMapHandle>, buildings: PlanBuilding[],
  heatOf: (b: PlanBuilding) => number | null, onOpen: () => void, flash: (message: string) => void) {
  const heatById = useMemo(() => Object.fromEntries(buildings.map((b) => {
    const heat = heatOf(b);
    return [b.id, heat === null ? null : Math.round(heat)];
  })), [buildings, heatOf]);
  const seed = useMemo(() => {
    const sorted = [...buildings].sort((a, b) => a.num - b.num);
    return { 'demo-2': sorted.slice(0, 2), 'demo-3': sorted.slice(2, 5) };
  }, [buildings]);
  const selection = useSelection(heatById, {
    'demo-2': seed['demo-2'].map((b) => b.id), 'demo-3': seed['demo-3'].map((b) => b.id),
  });
  const upload = useBuildingUpload();
  const byId = useMemo(() => new Map(buildings.map((b) => [b.id, b])), [buildings]);
  const memberBuildings = selection.members.map((id) => byId.get(id)).filter((b): b is PlanBuilding => !!b);
  const targets = useMemo(() => memberBuildings.map(centroid), [memberBuildings.map((b) => b.id).join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const starts = useMemo(() => Object.fromEntries(Object.entries(seed)
    .filter(([, list]) => list.length).map(([id, list]) => [id, centroid(list[0])])), [seed]);
  const network = useNetwork(selection.selectionId ?? '', targets, selection.summary.heatTotal,
    [...selection.members].sort().join(','), memberBuildings.map((b) => heatById[b.id] ?? null), starts);
  const [creating, setCreating] = useState(false);
  const [editingLegend, setEditingLegend] = useState(false);
  const editing = !!selection.activeTool || !!network.activeTool;
  useNetworkMap(mapRef, network, { blocked: !!selection.activeTool, onDragStart: onOpen });

  const reset = () => { selection.cancel(); network.cancel(); setEditingLegend(false); };
  const close = () => { reset(); setCreating(false); };
  const create = (draft: SelectionDraft) => {
    reset(); selection.create(draft); setCreating(false);
    flash(`Sélection « ${draft.name} » créée localement — aucune écriture réelle.`);
  };

  const layers = useMemo<LayerSpecification[]>(() => [
    { id: 'selection-building-members', type: 'line', source: 'buildings',
      filter: ['in', ['get', 'fid'], ['literal', selection.members]], paint: { 'line-color': UMR.map.pipe, 'line-width': 3 } },
    ...NETWORK_LAYERS,
  ], [selection.members]);

  const panel = (onClose: () => void) => {
    const closeAll = () => { close(); onClose(); };
    if (editingLegend) {
      return <DensityLegendPanel legend={network.density.legend} onSave={network.density.saveLegend} onClose={() => setEditingLegend(false)} />;
    }
    if (creating) return <SelectionFormPanel onClose={closeAll} onCancel={() => setCreating(false)} onSubmit={create} />;
    return (
      <SelectionPanel onClose={closeAll} upload={upload} network={network}
        onEditLegend={() => { if (!editing && !network.loading) setEditingLegend(true); }}
        selections={selection.selections} selectionId={selection.selectionId}
        consumption={selection.current?.consumption ?? 'Chaud'}
        onSelectionChange={(id) => { if (network.activeTool || network.loading) return; network.cancel(); selection.changeSelection(id); }}
        onSelectionActions={flash} onQuit={() => { reset(); selection.quit(); }}
        onCreate={() => { reset(); setCreating(true); }}
        summary={{ ...selection.summary, pipes: network.length }} activeTool={selection.activeTool} />
    );
  };

  return {
    panel, close, layers, editing, network,
    source: networkSource(network),
    instruction: network.instruction,
  };
}
